import React, { useEffect, useState, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { connect } from 'react-redux';
import Icon from '@mdi/react';
import { mdiMagnify, mdiChevronDown } from '@mdi/js';
import { makeApiCall, isWithinTradingHours, formatDate } from '../../../helpers/utils';
import EmptyState from '../../../components/pages/markets/component/empty-state';
import { useInterval } from '../../../helpers/custom-hooks';
import isEmpty from "lodash/isEmpty";
import { BQLink } from "../../atoms/bqlink";
import { isMobileOnly as isMobile } from "react-device-detect";
import RenderCards from './render-cards';
import FilterControls from "./filter-controls";
import { getFromDate } from "./heatmap-sectors-page";
import { DATE_FORMATS } from "../../../constants";
import './budget-impacts-page.m.css';

const PRIMARY_TABS_COUNT = 4;

const HeatmapStocksPage = ({ features }) => {
  const [currentTab, setCurrentTab] = useState('');
  const [currentSector, setCurrentSector] = useState(null);
  const [cardsData, setCardsData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tabs, setTabs] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [cardLoading, setCardLoading] = useState(false); // NEW: Track card loading state
  const [dropdownLabel, setDropdownLabel] = useState('More');
  const [showFilters, setShowFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState('low-to-high');
  const [compareRange, setCompareRange] = useState('1d');

  const [ref, inView] = useInView();
  const dropdownRef = useRef(null);

  useEffect(() => {
    try {
      makeApiCall(features?.heatmap?.sector_url).then((res) => {
        if (res && res?.length > 0) {
          setCurrentTab(res?.[0]?.EmbedCode.replace(/\s/g, '_').replace(/&/g, '_'));
          const data = [];
          res.forEach((item) => {
            let slug = '';
            if (!!item?.Link) {
              const url = new URL(item?.Link);
              slug = url.pathname;
            }
            data.push({
              name: item?.EmbedCode,
              key: item?.EmbedCode.replace(/\s/g, '_').replace(/&/g, '_'),
              hero_image: item?.FullImage || '',
              headline: item?.Title || '',
              slug,
              sectorId: item?.author,
            });
          });
          setTabs(data);
        }
      });
    } catch (e) {
      console.error('Error in fetching budget impacts tabs', e);
    }
  }, []);

  useEffect(() => {
    if (initialLoading && tabs.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const sector = urlParams.get('sector');
      const state = urlParams.get('state');
      const firstTab = !!sector ? tabs?.find(tab => tab?.name?.replace(/\s/g, '_')?.replace(/&/g, '_')?.toLowerCase() === sector) : tabs[0];
      if (!isEmpty(firstTab)) {
        setInitialLoading(false);
        setCurrentSector(firstTab.sectorId);
        setCurrentTab(firstTab.key);
        if (!!state && state === 'war') {
          setShowFilters(true);
          const fromDate = formatDate(getFromDate(compareRange), DATE_FORMATS.DATE_TIME_REVERSE);
          const toDate = formatDate(getFromDate(), DATE_FORMATS.DATE_TIME_REVERSE);
          getSectorData(firstTab.sectorId, true, false, fromDate, toDate);
        } else {
          getSectorData(firstTab.sectorId, true);
        }
        const currentPath = window.location.pathname;
        history.pushState(null, '', currentPath);
        setDropdownLabel(isMobile ? firstTab.name : !!sector && tabs?.slice(0, PRIMARY_TABS_COUNT)?.map(tab => tab?.name?.replace(/\s/g, '_')?.replace(/&/g, '_')?.toLowerCase())?.indexOf(sector) === -1 ? firstTab.name : 'More');
      }
    }
  }, [tabs]);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleScroll = (event) => {
      // If scroll happened INSIDE dropdown → ignore
      if (dropdownRef.current?.contains(event.target)) {
        return;
      }
      // Scroll happened outside → close dropdown
      setIsDropdownOpen(false);
    };
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    // Capture = true is required to detect scroll on all parents
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!initialLoading) {
      getSectorData(currentSector, false, true, formatDate(getFromDate(compareRange), DATE_FORMATS.DATE_TIME_REVERSE), formatDate(getFromDate(), DATE_FORMATS.DATE_TIME_REVERSE));
    }
  }, [compareRange]);

  useInterval(() => {
    if (showFilters) {
      getSectorData(currentSector, false, false, formatDate(getFromDate(compareRange), DATE_FORMATS.DATE_TIME_REVERSE), formatDate(getFromDate(), DATE_FORMATS.DATE_TIME_REVERSE));
    } else {
      getSectorData(currentSector);
    }
  }, 60000);

  const getSectorData = (id, initialLoading = false, tabSwitch = false, fromDate = '', toDate = '') => {
    if (
      (isWithinTradingHours(features?.trading_holidays, features?.special_trading_days) &&
        inView) ||
      initialLoading ||
      tabSwitch
    ) {
      if (initialLoading || tabSwitch) {
        setCardLoading(true);
      }
      let url = `${features?.heatmap?.stocks_data_url}?sectorId=${id}`;
      if (!!fromDate && !!toDate) {
        url += `&fromDate=${fromDate}&toDate=${toDate}`;
      }
      makeApiCall(url)
        .then((res) => {
          if (res?.data && res?.data?.length > 0) {
            setCardsData(res.data); // empty array is VALID
          } else {
            setCardsData([]);
          }
        })
        .catch(() => {
          setCardsData([]);
        })
        .finally(() => {
          //  ALWAYS stop loading
          if (initialLoading || tabSwitch) {
            setCardLoading(false);
          }
        });
    }
  };

  const selectTab = (e, tab, fromDropdown = false) => {
    setCurrentTab(tab.key);

    if (currentSector !== tab.sectorId) {
      if (showFilters) {
        getSectorData(tab.sectorId, false, true, formatDate(getFromDate(compareRange), DATE_FORMATS.DATE_TIME_REVERSE), formatDate(getFromDate(), DATE_FORMATS.DATE_TIME_REVERSE));
      } else {
        getSectorData(tab.sectorId, false, true);
      }
      setCurrentSector(tab.sectorId);
    }

    if (fromDropdown) {
      setDropdownLabel(tab.name);
    } else {
      setDropdownLabel('More');
    }

    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const takeToStockPage = (symbol, entityId) => {
    try {
      makeApiCall(`/api/v1/entities?type=stock&q=${symbol}&limit=30`).then((res) => {
        if (
          res.entities &&
          res.entities.length > 0 &&
          res.entities.find((ent) => ent['nse-code'] === symbol || ent['stock-id'] === +entityId)
        ) {
          window.open(
            `/stock/${res.entities.find((ent) => ent['nse-code'] === symbol || ent['stock-id'] === +entityId)['stock-id']}/${res.entities.find((ent) => ent['nse-code'] === symbol || ent['stock-id'] === +entityId).slug.replace('-stock', '')}`,
            '_blank',
          );
        }
      });
    } catch (e) {
      console.error('Error in fetching stock entity', e);
    }
  };

  const primaryTabs = !isMobile ? tabs.slice(0, PRIMARY_TABS_COUNT) : [];
  const dropdownTabs = isMobile ? tabs : tabs.slice(PRIMARY_TABS_COUNT);

  const isDropdownActive =
    dropdownLabel !== 'More' || dropdownTabs.some((tab) => tab.key === currentTab);

  const filteredDropdownSectors = dropdownTabs?.filter((tab) =>
    tab.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <>
      <div ref={ref} styleName="budget-impacts-ctx" className="section-fluid">
        <div styleName="budget-impacts-inner-ctx" className="container">
          {/* Heading Section */}
          <div styleName={`headline-ctx heatmap-war`}>
            {initialLoading ? (
              <div styleName="title-subtitle">
                <div styleName="title-loading"></div>
                <div styleName="subtitle-loading"></div>
              </div>
            ) : (
              <React.Fragment>
                {showFilters ? (
                  <div styleName="title-subtitle">
                    <div styleName="title">{features?.heatmap?.texts?.custom?.title}</div>
                    <div styleName="subtitle" dangerouslySetInnerHTML={{__html: features?.heatmap?.texts?.custom?.subtitle.replace('#page', features?.heatmap?.texts?.stocks.toLowerCase())}}></div>
                  </div>
                ) : (
                  <div styleName="original-title-ctx">
                    <div styleName="title">{features?.heatmap?.texts?.default?.title}</div>
                    <div styleName="drop-list">{features?.heatmap?.texts?.stocks}</div>
                  </div>
                )}
              </React.Fragment>
            )}
            {showFilters && (
              <FilterControls
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                compareRange={compareRange}
                setCompareRange={setCompareRange}
                getFromDate={getFromDate}
              />
            )}
          </div>

          {/* FIXED: Show shimmer only on initial load */}
          {initialLoading ? (
            <>
              {/* Tabs Header Shimmer */}
              <div styleName="tabs-header">
                <div styleName="tabs-container">
                  <div styleName="tabs-wrapper tab-loading">
                    <div styleName="sectors" className="mobile-only"></div>
                    {!isMobile &&
                      Array.apply(null, Array(5)).map((_, idx) => (
                        <div key={idx} styleName="sectors" className="desktop-only"></div>
                      ))}
                  </div>
                </div>
              </div>

              {/* Story Shimmer */}
              {!showFilters && (
                <div styleName="story loading">
                  <div styleName="first-row"></div>
                  <div styleName="second-row"></div>
                  <div styleName="third-row"></div>
                </div>
              )}

              {/* Card Section Shimmer */}
              <div styleName="story-heatmap-ctx">
                <div styleName="cards-ctx">
                  <div styleName="cards">
                    {Array.apply(null, Array(16)).map((_, idx) => (
                      <div key={idx} styleName="card card-loading">
                        <div styleName="first-row"></div>
                        <div styleName="second-row"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Tabs Header */}
              <div styleName="tabs-header">
                <div styleName="tabs-container">
                  <div styleName="tabs-wrapper">
                    {/* FIRST 4 TABS */}
                    {primaryTabs.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={(e) => selectTab(e, tab, false)}
                        styleName={`tab-btn ${currentTab === tab.key ? 'active' : ''}`}
                      >
                        {tab.name}
                      </button>
                    ))}

                    {/* DROPDOWN */}
                    {dropdownTabs.length > 0 && (
                      <div styleName="dropdown" ref={dropdownRef}>
                        <button
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                          styleName={`tab-btn dropdown-toggle ${isDropdownActive ? 'active' : ''}`}
                        >
                          {dropdownLabel}

                          <div styleName={`chevron ${isDropdownOpen ? 'open' : ''}`}>
                            <Icon path={mdiChevronDown} size="24px" />
                          </div>
                        </button>

                        {isDropdownOpen && (
                          <div styleName="dropdown-menu">
                            <div styleName="dropdown-search">
                              <div styleName="search-icon">
                                <Icon path={mdiMagnify} size="20px" color="#A5AFB4" />
                              </div>
                              <input
                                type="text"
                                placeholder="Search Sectors"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                            </div>

                            <div styleName="dropdown-list">
                              {filteredDropdownSectors.map((tab) => (
                                <button
                                  key={tab.key}
                                  onClick={(e) => selectTab(e, tab, true)}
                                  styleName={`dropdown-item ${currentTab === tab.key ? 'active' : ''}`}
                                >
                                  {tab.name}
                                </button>
                              ))}

                              {filteredDropdownSectors.length === 0 && (
                                <div styleName="no-results">No sectors found</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Story Section */}
              {!showFilters && !!tabs.find((tab) => tab.key === currentTab).hero_image && (
                <BQLink href={tabs.find((tab) => tab.key === currentTab).slug} target="_blank">
                  <div
                    styleName="story"
                    style={{
                      background: `${isMobile ? 'linear-gradient(179deg, #00000000 0%, #262626 100%)' : 'linear-gradient(269deg, #00000000 0%, #262626 50%)'}, url(${tabs.find((tab) => tab.key === currentTab).hero_image}) no-repeat`,
                    }}
                  >
                    <div styleName="story-headline-ctx">
                      <div styleName="current-tab-name">
                        {tabs.find((tab) => tab.key === currentTab).name}
                      </div>
                      <div styleName="headline">
                        {tabs.find((tab) => tab.key === currentTab).headline}
                      </div>
                    </div>
                  </div>
                </BQLink>
              )}

              {/* Card Section */}
              <div styleName="story-heatmap-ctx">
                <div styleName="cards-ctx">
                  {/* FIXED: Show card shimmer when loading cards */}
                  {cardLoading ? (
                    <div styleName="cards">
                      {Array.apply(null, Array(12)).map((_, idx) => (
                        <div key={idx} styleName="card card-loading">
                          <div styleName="first-row"></div>
                          <div styleName="second-row"></div>
                        </div>
                      ))}
                    </div>
                  ) : cardsData.length === 0 ? (
                    <EmptyState width="280px" height="280px" />
                  ) : (
                    <div styleName="cards">{RenderCards(cardsData, takeToStockPage, '', sortOrder)}</div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  features: state.qt.config.features,
});

export default connect(mapStateToProps)(HeatmapStocksPage);
